import { schemas } from '@repo/contracts'
import { apiErrorHandler } from '../errorHandler'
import { parseDataOfType } from '../../common/parseDataType'
import { optionalProjectId } from '../../common/optionalFields'
import type { z } from 'zod'

// -- UI enum value map (kept hand-written; RagFilePage references e.g. RagFileUploadState.Processing).
// The generated z.enum(...) inside apihttp_RagFileItemV2 governs wire parsing; this map is a UI
// convenience and stays type-compatible (same string-literal union). --

export const RagFileUploadState = {
  Unknown: 'unknown',
  Uploading: 'uploading',
  Processing: 'processing',
  Complete: 'complete',
  Error: 'error'
} as const

export type RagFileUploadState = (typeof RagFileUploadState)[keyof typeof RagFileUploadState]

// -- Wire types (generated; @repo/contracts is the source of truth) --

export type RagFile = z.infer<typeof schemas.apihttp_RagFileItemV2>
export type GetRagFileResponse = z.infer<typeof schemas.apihttp_SearchFileV2Response>
export type FetchRagFiles = z.input<typeof schemas.apihttp_SearchRequestV2>
export type AddRagFileResult = {
  fileId: number
  fileName: string
  fileUploadState: RagFileUploadState
  reused: boolean
}

const defaultFetchParams: FetchRagFiles = {
  currentPage: 1
}

export async function fetchRagFiles(params: Partial<FetchRagFiles> = {}): Promise<GetRagFileResponse> {
  const body: FetchRagFiles = { ...defaultFetchParams, ...params }
  const res = await fetch('/api/content/file/searchv2', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body)
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_SearchFileV2Response)
}

async function readFileWithSha256(file: File): Promise<{ bytes: ArrayBuffer; sha256: string }> {
  const bytes = await file.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return {
    bytes,
    sha256: Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
  }
}

async function findRagFileByHash(sha256: string, projectId?: number): Promise<AddRagFileResult | undefined> {
  const res = await fetch('/api/content/file/searchv2/exists/hash', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ value: sha256, ...optionalProjectId(projectId) })
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  const existing = parseDataOfType(data, schemas.apihttp_ExistsFileSearchResponse)
  if (!existing.exists) return undefined
  if (existing.fileID === undefined || existing.fileName === undefined || existing.fileUploadState === undefined) {
    throw new Error('The existing file could not be identified.')
  }
  return {
    fileId: existing.fileID,
    fileName: existing.fileName,
    fileUploadState: existing.fileUploadState,
    reused: true
  }
}

export async function addRagFileWithResult(
  file: File,
  description: string,
  projectId?: number,
  tagIds: Array<number> = []
): Promise<AddRagFileResult> {
  const { bytes, sha256 } = await readFileWithSha256(file)
  const existing = await findRagFileByHash(sha256, projectId)
  if (existing !== undefined) {
    if (tagIds.length > 0) {
      const response = await fetchRagFiles({ docId: existing.fileId, currentPage: 1, ...optionalProjectId(projectId) })
      const current = response.items.find((item) => item.fileId === existing.fileId)
      if (!current) throw new Error('The existing file could not be loaded to apply its tags.')
      const mergedTagIds = [...new Set([...current.tags.map((tag) => tag.tagId), ...tagIds])]
      if (mergedTagIds.length !== current.tags.length) {
        await updateRagFile(current.fileId, current.description, projectId, mergedTagIds)
      }
    }
    return existing
  }

  const registerRes = await fetch('/api/content/file', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      fileName: file.name,
      description,
      tagIds,
      ...optionalProjectId(projectId),
      sha256
    })
  })
  await apiErrorHandler(registerRes)
  const registerData: unknown = await registerRes.json()
  const { fileID, preSignedUrl } = parseDataOfType(registerData, schemas.apihttp_AddFileResponse)

  const uploadRes = await fetch(preSignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: bytes
  })
  if (!uploadRes.ok) {
    throw new Error(`File upload failed: ${uploadRes.statusText}`)
  }
  return {
    fileId: fileID,
    fileName: file.name,
    fileUploadState: RagFileUploadState.Uploading,
    reused: false
  }
}

export async function addRagFile(
  file: File,
  description: string,
  projectId?: number,
  tagIds: Array<number> = []
): Promise<number> {
  return (await addRagFileWithResult(file, description, projectId, tagIds)).fileId
}

export async function updateRagFile(
  fileId: number,
  description: string,
  projectId: number | undefined,
  tagIds: Array<number>
): Promise<void> {
  const res = await fetch(`/api/content/file/${fileId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(
      schemas.apihttp_UpdateFileRequest.parse({ description, tagIds, ...optionalProjectId(projectId) })
    )
  })
  await apiErrorHandler(res)
}

export async function deleteRagFile(fileId: number, projectId?: number): Promise<void> {
  const url =
    projectId !== undefined ? `/api/content/file/${fileId}?projectId=${projectId}` : `/api/content/file/${fileId}`
  const res = await fetch(url, {
    method: 'DELETE',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
}

export async function getRagFileDownloadUrl(fileId: number): Promise<string> {
  const res = await fetch(`/api/content/file/${fileId}/download`, {
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  const parsed = parseDataOfType(data, schemas.apihttp_FileDownloadResponse)
  return parsed.downloadUrl
}

export async function retryRagFileProcessing(fileId: number): Promise<void> {
  const res = await fetch(`/api/content/file/${fileId}/retry-processing`, {
    method: 'POST',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
}
